import React, { Component, Profiler } from "react";
import { toast } from "react-toastify";
import { interpolateEntityFields } from "../markdown/interpolation";
import { DEV, getConfig } from "./configMgr";
import * as Sentry from "@sentry/react";
import { Buffer } from "buffer";
import { generateExternalLinks } from "./links";

//const UNICODE_PUNCTUATION_RE = /[\u2000-\u206F\u2E00-\u2E7F\\'!"#$%()*+,\-./:;=?@[\]^_`{|}~]/g;
export const RE_CATEGORY_PREFIXED_ID = /^(?!https?:)[a-z][-_a-z0-9]+:[a-z][-a-z]+:.*$/;
export const RE_PLEX_ID = /^(?!https?:)[a-z][-_a-z0-9]+:[^\s,;'"\\]+$/;
export const RE_TERM_LABEL = /\s+label\s*[:=]\s*/;

export const DELETE_MARKER = "ⓧ";
export const NBSP = "\u00A0";
export const TS = "\n";
export const {legacyGS, legacyGPSA} = getConfig();

export const elideString = (str, len, end = "...") => {
  return str.length >= len + end.length ? str.substring(0, len) + end : str;
};

// Smart truncation that breaks at word boundaries (space, closing brace/parens)
// Looks for the nearest word break before maxLen, falling back to hard truncation if needed
export const smartTruncate = (text, maxLen) => {
  if (!text || text.length <= maxLen) {
    return text;
  }

  // Try to find a word break character (space, ), }, ]) near the truncation point
  // Search backwards from maxLen to find the last word break
  const searchStart = Math.max(0, maxLen - 10); // Look within 10 chars before maxLen
  let breakPoint = -1;

  for (let i = maxLen - 1; i >= searchStart; i--) {
    const char = text[i];
    if (char === ' ' || char === ')' || char === '}' || char === ']') {
      breakPoint = i;
      break;
    }
  }

  // If we found a good break point, use it; otherwise use hard truncation
  if (breakPoint > searchStart) {
    return text.substring(0, breakPoint) + "...";
  } else {
    return text.substring(0, maxLen) + "...";
  }
};

const PREFERRED_ALIAS_FORMATS = [
  // Single words, no digits
  /^[^\d\W]+$/,
  // with dashes (no inchi)
  /^(?![A-Z]{14}-[A-Z]{10}-[A-Z]|S?CHEMBL|CID|UCI)[^\d\W]([^\d\W]|-)+$/,
  /^(?![A-Z]{14}-[A-Z]{10}-[A-Z]|S?CHEMBL|CID|UCI)([^\d\W]|-)+$/,
  // with spaces or commas
  /^(?![A-Z]{14}-[A-Z]{10}-[A-Z]|S?CHEMBL|CID|UCI)[^\d\W]([^\d\W]|[-\s,])+$/,
  /^(?![A-Z]{14}-[A-Z]{10}-[A-Z]|S?CHEMBL|CID|UCI)([^\d\W]|[-\s,])+$/,
  // with parens/brackets/plus
  /^(?![A-Z]{14}-[A-Z]{10}-[A-Z]|S?CHEMBL|CID|UCI)[^\d\W]([^\d\W]|[-+\s,()\][])+$/,
  /^(?![A-Z]{14}-[A-Z]{10}-[A-Z]|S?CHEMBL|CID|UCI)([^\d\W]|[-+\s,()\][])+$/,
  // with numbers
  /^(?![A-Z]{14}-[A-Z]{10}-[A-Z]|S?CHEMBL|CID|UCI)\D[-\w+\s,()\][]+$/,
  /^(?![A-Z]{14}-[A-Z]{10}-[A-Z]|S?CHEMBL|CID|UCI)[-\w+\s,()\][]+$/,
  // preferred datasets if no other matches
  // /^(CHEMBL\d+)$/,
  /^(CHEMBL|CID)\d+$/,
  // anything else
  /^(.*)$/,
]

export const countNonwordChars = s => {
  return s.replace(/([^\d\W]|[-\s])/g, "").length;
};

export const countDigits = s => {
  return s.replace(/\D/g, "").length;
};

export const getBestName = (entity, short = false) => {
  if (!entity) {
    return "<null>";
  }
  const noTitle = DEV ? " <no title>" : "";
  if (entity.entity) {
    entity = entity.entity;
  }
  if (entity.label) {
    if (short && entity.category === 'target' && entity.gene_symbol) {
      return entity.gene_symbol;
    }
    if (short && entity.aliases?.length > 0) {
      // Only prefer a short alias if the label looks like a systematic/IUPAC chemical name
      // (long, containing digits or brackets typical of chemical nomenclature)
      const isIupacLike = entity.label.length > 20 && /[\d\[\(]/.test(entity.label);
      if (isIupacLike) {
        const shortest = entity.aliases
          .filter(a => a.length > 3)
          .reduce((a, b) => a.length <= b.length ? a : b, entity.label);
        if (shortest.length < entity.label.length) {
          return shortest;
        }
      }
    }
    return entity.label;
  }
  if (entity.title) {
    return entity.title;
  }
  if (entity.category === 'target' && entity.gene_symbol) {
    return `${entity.gene_symbol}${noTitle}`;
  }
  const aliases = (entity.aliases || []).slice();
  if (aliases.length > 0) {
    if (short) {
      return aliases.reduce((a, b) => a.length <= b.length ? a : b);
    }
    for (let fmt of PREFERRED_ALIAS_FORMATS) {
      const candidates = []
      aliases.forEach(alias => {
        if (alias.match(fmt)) {
          candidates.push(alias);
        }
      });
      if (candidates.length > 0) {
        const sorted = candidates.sort((a, b) => {
          // Prefer CHEMBL > CID > SCHEMBL/BDB/HMDB > UCI
          if (/^(CHEMBL|CID)/i.test(a) && /^(CHEMBL|CID)/i.test(b)) {
            return a - b;
          }
          const delta = countNonwordChars(a) - countNonwordChars(b);
          const delta2 = countDigits(a) - countDigits(b);
          return delta + delta2;
        });
        return `${sorted[0]}${noTitle}`;
      }
    }
  }
  if (entity.iupac) {
    return `${entity.iupac}${noTitle}`;
  }
  return null;
};

export const saveBlobContent = (blobContent, filename) => {
  const blob = new Blob([blobContent], {type: blobContent.type});
  saveBlob(blob, filename);
};

export const formatTimestamp = (ts, full = false) => {
  // Timestamp is in milliseconds
  if (!ts) {
    return DEV ? `<ts=${ts}>` : "";
  }
  if (ts < 10000000000) {
    const ms = ts % 1000;
    const ss = Math.floor(ts / 1000) % 60;
    const mm = Math.floor(ts / 1000 / 60) % 60;
    const hh = Math.floor(ts / 1000 / 3600);
    return hh ? hh < 10 ? `${hh}h${mm.toString().padStart(2, '0')}m`
                        : `${hh}h`
              : mm ? mm < 10 ? `${mm}m${ss.toString().padStart(2, '0')}s`
                             : `${mm}m`
                   : ss < 10 ? `${Math.floor(ss)}.${ms.toString().padStart(3, '0')}s`
                             : `${ss}s`;
  }
  const date = new Date(ts);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hour = String(date.getHours()).padStart(2, "0");
  const minute = String(date.getMinutes()).padStart(2, "0");
  const second = String(date.getSeconds()).padStart(2, "0");
  return full ? `${year}${month}${day}T${hour}:${minute}:${second}` : `${hour}:${minute}:${second}`;
};

export const saveBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  // navigator.msSaveOrOpenBlob - available for IE only
  if (navigator.msSaveOrOpenBlob) {
    navigator.msSaveOrOpenBlob(blob, filename);
  } else {
    const a = document.createElement("a");
    a.setAttribute("style", "display: none");
    a.setAttribute("target", "_black");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }
};

export const safeDOMID = id => {
  return id?.replace(/[^-A-Z0-9_]/gi, "-").replace(/-{2,}/, "-");
};

export const wait = async (ms) => new Promise(resolve => setTimeout(resolve, ms));

export const getCategoryKey = (catSrcID) => {
  return RE_CATEGORY_PREFIXED_ID.test(catSrcID) && catSrcID.split(':')[1];
};

export const isNumericID = id => {
  return /^[A-Z]{2,}[-:0-9]{3,}$/i.test(id);
}

export const isInchi = id => {
  return /^((InChI=)[^J][0-9BCOHNSPrIFla+\-()\\/,pqbtmsih]{6,})$/i.test(id);
}

export const isInchiKey = id => {
  return /^[A-Z]{{14}}-[A-Z]{{10}}-[A-Z]?/.test(id);
}

// smiles:/query: IDs may contain backslashes and other characters excluded from RE_PLEX_ID
const RE_SMILES_QUERY_ID = /^(?:smiles|query):.+$/;
export const isPlexId = id => {
  return (RE_PLEX_ID.test(id) || RE_SMILES_QUERY_ID.test(id)) && !RE_CATEGORY_PREFIXED_ID.test(id);
}

export const getExternalID = id => {
  return (RE_CATEGORY_PREFIXED_ID.test(id) && id.slice(id.indexOf(':') + 1)) || id;
}

export const extractLabel = term => {
  if (RE_TERM_LABEL.test(term)) {
    const [id, label] = term.split(RE_TERM_LABEL);
    return [id.trim(), label.trim()];
  }
  return [term , null];
};

export const saveCustomLabel = (id, label) => {
  try {
    const key = `label-${id}`;
    if (label) {
      localStorage[key] = label.trim();
    } else {
      localStorage.removeItem(key);
    }
  }
  catch(e) {
    console.error(`Could not access local storage ${e}`);
  }
};

export const getCustomLabels = () => {
  return Object.keys(localStorage)
    .filter(key => key.startsWith("label-"))
    .reduce((result, key) => {
      const id = key.substring("label-".length);
      result[id] = localStorage.getItem(key);
      return result;
    }, {});
};

export const getCustomLabel = id => {
  try {
    return localStorage[`label-${id}`]?.trim();
  }
  catch(e) {
    console.error(`Could not access local storage ${e}`);
  }
};

// Legacy display names for pseudo-categories that may be missing from older metadata versions
// These definitions existed in data versions prior to 1.5.0 where gds/gwps were separate categories
const LEGACY_PRE_1_5_CATEGORY_NAMES = {
  'gds': { name: 'RNA Profile', tab_name: 'RNA Profiles' },
  'gwps': { name: 'Perturb-Seq', tab_name: 'PerturbSeq' },
  'superfamily': { name: 'Protein Family', tab_name: 'Protein Families' }
};

export const patchEntity = (entity, categories = [], dataset = {},
                            templates = {}, preferredNames= {},
                            datasetsOrGetter = null) => {
  const getDatasets = typeof datasetsOrGetter === 'function'
    ? datasetsOrGetter
    : () => datasetsOrGetter;
  if (entity['_patched']) {
    return entity;
  }
  if (entity.tags && entity.tags[0] !== entity.category) {
    entity.baseCategory = entity.category;
    entity.category = entity.tags[0] || entity.category;
  }
  else {
    entity.baseCategory = entity.category;
  }
  const cat = categories[entity.category] || categories[entity.baseCategory] || LEGACY_PRE_1_5_CATEGORY_NAMES[entity.category];
  if (!cat && entity.category !== 'archived' && Object.keys(categories).length > 0) {
    console.error(`No category corresponding to '${entity.category}':`, entity, categories);
  }
  // Singular; coerce to string since entity.category may be a non-string value from server data
  const rawCategoryName = (cat && cat.name) || entity.category;
  entity.categoryName = rawCategoryName != null && typeof rawCategoryName !== 'string'
    ? String(rawCategoryName) : rawCategoryName;
  // Plural (e.g. tab name)
  const rawCategoryLabel = (cat && cat.tab_name) || entity.categoryName;
  entity.categoryLabel = rawCategoryLabel != null && typeof rawCategoryLabel !== 'string'
    ? String(rawCategoryLabel) : rawCategoryLabel;

  if (typeof (entity.aliases) === 'string') {
    entity.aliases = entity.aliases.split(';');
  }
  // Keep only a single SMILES encoding
  if (entity.smiles && typeof (entity.smiles) !== "string") {
    entity.smiles = entity.smiles[0];
  }

  const prefName = preferredNames[entity.id];
  if (prefName) {
    entity['preferred-name'] = prefName;
  }

  const cleanTitle = (t) => {
    return t ? t.replace(/;(?!=\s)/g, "; ")
      .replace(/^\[(.*)]$/, "$1") : t;
  } ;

  // Clean up titles for readability
  entity.title = cleanTitle(entity.title);
  entity.iupac = cleanTitle(entity.iupac);
  entity['best-alias'] = cleanTitle(getBestName(entity));
  // Decode and interpolate properties
  entity = interpolateEntityFields(templates, entity, dataset);

  // Convert to a list to facilitate display
  if (entity.activity) {
    entity.activity = Object.entries(entity.activity);
  }

  Object.entries(entity).forEach(([k, v]) => {
    if (isTruthy(v)) {
      if (!entity.flags) {
        entity.flags = [k];
      }
      else {
        entity.flags.push(k);
      }
    }
  });
  ["description", "abstract"].forEach(k => {
    if (entity[k]) {
      const s = entity[k];
      entity[k] = entity[k]
        // This has since been fixed up in the source data
        .replace(/<br\/>\s*<db_xref db="INTERPRO" dbkey="(IPR\d+)"\/>\s*<br\/>\s*/gi,
                 "[$1](https://www.ebi.ac.uk/interpro/entry/InterPro/$1 \"$1\")")
        .replace(/<br\/>\s*<db_xref db="EC?" dbkey="([\d.]+)"\/>\s*<br\/>\s*/gi,
                 "[EC:$1](https://pubchem.ncbi.nlm.nih.gov/protein/EC:$1 \"EC:$1\")")
        // As of 20250326, the IPR source data is pre-processed into markdown
        .replace(/\[IPR\d+]\(ipr:(IPR\d+)\s*\)/gi, "[$1](https://www.ebi.ac.uk/interpro/entry/InterPro/$1 \"$1\")")
        .replace(/\[[\d.]+]\((EC:[\d.]+)\s*\)/gi, "[$1](https://pubchem.ncbi.nlm.nih.gov/protein/$1 \"$1\")")
        .replace(/\*\s*<br\/>\s*/gi, "* ")
        .replace(/<br\/>(<br\/>|\s)*<br\/>/gi, "\n\n")
        .trim();
    }
  });
  const customLabel = getCustomLabel(entity.id);
  if (customLabel) {
    if (!entity.label) {
      entity.label = customLabel;
    }
    else if (entity.label.indexOf(customLabel) === -1) {
      entity.label = `[${customLabel}] ${entity.label}`;
    }
  }
  // Define a custom object with lazy loaded source links
  const patchedEntity = {
    _patched: true,
    ...entity,
  };

  // Define links as a non-enumerable lazy getter to prevent access during serialization
  // (e.g., console.log, JSON.stringify, Sentry breadcrumbs)
  Object.defineProperty(patchedEntity, 'links', {
    enumerable: false,  // Won't show up in for...in, Object.keys(), or JSON.stringify
    configurable: true,
    get() {
      const datasets = getDatasets();
      return generateExternalLinks(entity, datasets);
    }
  });

  return patchedEntity;
};

export const withProfiler = (WrappedComponent) => (props) => (<Profiler><WrappedComponent {...props} /></Profiler>);

// Wrap a component in this to debug when properties change
export function withPropsChecker(WrappedComponent, defaultName= "?") {
  return class PropsChecker extends Component {
    componentDidUpdate(lastProps) {
      Object.keys(lastProps)
        .filter(key => {
          return lastProps[key] !== this.props[key];
        })
        .forEach(key => {
          const name = WrappedComponent.displayName || WrappedComponent.name || defaultName;
          console.debug(`changed property "${key}" on ${name} from`, lastProps[key], 'to', this.props[key]);
        });
    }
    render() {
      return <WrappedComponent {...this.props} />;
    }
  };
}

// extraFlavors: { [mimeType]: string } — additional clipboard flavors to attempt alongside text/plain.
// Custom web MIME types (prefixed "web ") are only supported in Chrome/Edge; other browsers silently drop them.
// If multi-flavor write fails, falls back to text/plain-only and calls onFallback (if provided).
export const copyToClipboard = (text, onSuccess = null, extraFlavors = null, onFallback = null) => {
  const writeToClipboard = (dataTypes) => {
    const item = new ClipboardItem(dataTypes);
    return navigator?.clipboard?.write([item]);
  };

  const dataTypes = {
    "text/plain": new Blob([text], {type: 'text/plain'}),
  };

  if (extraFlavors && Object.keys(extraFlavors).length > 0) {
    for (const [mime, value] of Object.entries(extraFlavors)) {
      dataTypes[mime] = new Blob([value], {type: mime});
    }
    writeToClipboard(dataTypes)
      .then(() => {
        if (onSuccess) onSuccess();
        else toast.success("Copied to clipboard");
      })
      .catch(() => {
        // Multi-flavor failed (unsupported browser) — fall back to text/plain only
        writeToClipboard({"text/plain": dataTypes["text/plain"]})
          .then(() => {
            if (onFallback) onFallback();
            else if (onSuccess) onSuccess();
            else toast.success("Copied to clipboard");
          })
          .catch((error) => {
            toast.error(`Clipboard write is unavailable (${error})`);
          });
      });
  } else {
    writeToClipboard(dataTypes)
      .then(() => {
        if (onSuccess) onSuccess();
        else toast.success("Copied to clipboard");
      })
      .catch((error) => {
        toast.error(`Clipboard write is unavailable (${error})`);
      });
  }
}

export const naturalSort = (a, b) => {
  if (!a) {
    return !b ? 0 : 1;
  }
  return a.localeCompare(b, undefined, {numeric: true, ignorePunctuation: true, sensitivity: "base"});
};

export const RE_PREFIXED = /^([^:]+):.*$/i;
export const RE_INT_ID = /^[^:]+:(\d+)$/i;
export const RE_DATASET_ID = /^([-a-z0-9]+):(.*)$/;

export const splitID = id => [datasetPrefix(id), getExternalID(id)];

export const stripDataset = id => RE_DATASET_ID.test(id) ? id.replace(RE_DATASET_ID, '$2') : id;

export const datasetPrefix = id => RE_PREFIXED.test(id) && id.replace(RE_PREFIXED, '$1');

export const isInteger = (s) => {
  return Number.isInteger(+(typeof s === 'string' ? s.trim() : s));
};

export const formatMultipleItemTitle = (items, maxItems = null) => {
  if (!maxItems) {
    maxItems = 3;
  }
  const names = items.map(x => typeof(x) === "string" ? x : getBestName(x, maxItems != null))
  if (items.length > maxItems + 1) {
    const explicit = names.slice(0, maxItems);
    return `${explicit.join(", ")} and ${items.length - explicit.length} others`;
  }
  return `${names.join(", ")}`;
}

// TODO: move title setting to Helmet (once helmet errors get fixed)
export const setPageTitle = (basis = "Plex Search", maxItems = 3) => {
  if (typeof(basis) === "string") {
    document.title = basis;
  }
  else if (basis?.length) {
    document.title = formatMultipleItemTitle(basis, maxItems)
  }
  else {
    document.title = "Plex Search";
  }
};

export function isTruthy(s) {
  return s === true || (typeof(s) === "string" && ["y", "yes", "true", "t"].includes(s.toLowerCase()));
}

export const isImage = file => {
  return /^image\//.test(file.type);
};
export const fileKey = file => {
  if (!file) {
    throw new TypeError(`File must be non-null`)
  }
  return `${file.name}:${file.size}:${file.lastModified}`;
};
export const areSetsEqual = (a, b) => a.size === b.size && [...a].every(value => b.has(value));

// Uploads that neither complete nor error within this window are killed via xhr.timeout —
// without it a stalled request never fires onload/onerror and the attachment hangs forever.
const UPLOAD_TIMEOUT_MS = 60000;

export const xhrUpload = async (url, file, onProgress = (uploadState) => {
}, authData = null) => {
  const state = {file, progress: 0, active: true};
  onProgress(state);
  return new Promise((resolve, reject) => {
    if (DEV && url.startsWith("/")) {
      url = `http://localhost:5000${url}`;
    }
    const formData = new FormData();
    formData.append('file', file);
    const xhr = new XMLHttpRequest();
    state.xhr = xhr;
    xhr.open("POST", url, true);
    xhr.timeout = UPLOAD_TIMEOUT_MS;
    Object.entries(authData.headers || {}).forEach(([k, v]) => {
      xhr.setRequestHeader(`${k}`, `${v}`);
    })

    const getResponse = xhr => {
      const raw = xhr.response;
      if (xhr.responseType === "" && typeof (raw) === "string" && (raw[0] === "{" || raw === "null")) {
        return ["json", JSON.parse(raw)];
      }
      return [xhr.responseType, raw];
    };

    onProgress(state);
    xhr.upload.onprogress = e => {
      state.event = e;
      if (e.lengthComputable) {
        state.progress = Math.floor(e.loaded * 100 / e.total);
      } else {
        state.progress += (e.total - state.progress) / 2;
      }
      onProgress(state);
    };
    xhr.onerror = (e) => {
      state.event = e;
      state.error = e;
      state.status = xhr.status;
      state.active = false;
      //console.log("xhr.onerror");
      reject(state);
    };
    xhr.ontimeout = (e) => {
      state.event = e;
      state.error = e;
      state.timeout = true;
      state.status = xhr.status;
      state.active = false;
      //console.log("xhr.ontimeout");
      reject(state);
    };
    xhr.onabort = (e) => {
      state.event = e;
      state.abort = true;
      state.active = false;
      //console.log("xhr.onabort");
      reject(state);
    };
    xhr.onload = e => {
      const [responseType, response] = getResponse(xhr);
      state.event = e;
      state.status = xhr.status;
      state.active = false;
      //console.log("xhr.onload", xhr, state);
      onProgress(state);
      if (xhr.readyState === xhr.DONE) {
        state.response = response;
        if (xhr.status && xhr.status < 400 && (responseType !== "json" || !response.status || response.status < 400)) {
          //console.log("xhr complete", xhr, state);
          resolve(state)
        } else {
          state.error = e;
          //console.log("xhr.onload(error)", xhr, state);
          reject(state);
        }
      }
    }
    xhr.send(formData);
  });
}

export const uploadFile = async (uploadURL, file, authData,
                                 onError = error => {},
                                 onProgress = (file, progress) => {},
                                 onComplete = (file, response) => {}) => {
    //console.log(`uploadFile: ${file.name}`);
    return xhrUpload(uploadURL, file, onProgress, authData)
      .then(state => {
        onComplete(file, state.response);
      })
      .catch(state => {
        //console.log("uploadFile.catch", state);
        if ((state.status === 401 || state.status === 403) && authData) {
          // Refresh credentials and retry
          //console.log("Refresh creds")
          return authData.refresh()
            .then(newAuthData => uploadFile(uploadURL, file, newAuthData, onError, onProgress, onComplete))
            .catch(error => {
              state.error = error;
              onError(state);
            })
        }
        else {
          onError(state);
        }
      });
  };

/**
 * Capture a message or exception to Sentry with optional attachments and context
 *
 * @param {string} message - The message or error description
 * @param {string|object} levelOrOptions - Severity level (info, warning, error, etc.) or options object
 * @param {object} data - Additional data to attach (legacy parameter, use options.data instead)
 * @returns {Promise<string>} - Promise resolving to the Sentry event ID
 *
 * Options object:
 * - level: Severity level (default: "error")
 * - asException: Send as exception instead of message (default: false)
 * - errorName: Custom error name for exceptions (default: "UserException")
 * - fingerprint: Custom fingerprint array to control event grouping (default: auto)
 * - tags: Object with tags to add to the event
 * - context: Object with context data
 * - attachments: Array of attachment objects {filename, data, contentType}
 * - data: Data to attach as data.json (legacy)
 */
export const captureMessage = (message, levelOrOptions = "error", data = null) => {
  try {
    let options = {};
    if (typeof levelOrOptions === "string") {
      options.level = levelOrOptions;
      options.data = data;
    } else {
      options = levelOrOptions || {};
    }

    const {
      level = "error",
      asException = false,
      errorName = null,
      fingerprint = null,
      tags = {},
      context = {},
      extra = null,
      attachments = [],
      data: optionsData = null,
      captureStackTrace = true
    } = options;

    const client = Sentry.getClient();
    const clientOptions = client?.getOptions();

    // CRITICAL FIX: Sentry SDK disables manual capture calls for localhost
    // Use console.error workaround which bypasses the enabled check
    if (!clientOptions?.enabled) {
      const error = new Error(message);
      error.name = errorName || (asException ? "UserException" : "UserMessage");
      if (Object.keys(tags).length > 0) error.tags = tags;
      if (Object.keys(context).length > 0) error.context = context;
      if (optionsData) error.data = optionsData;
      if (attachments.length > 0) error.attachments = attachments;
      console.error(error);
      return null;
    }

    let eventId;
    Sentry.withScope(scope => {
      if (fingerprint) scope.setFingerprint(fingerprint);

      const currentUser = Sentry.getCurrentScope().getUser();
      if (!currentUser) {
        const lastUsername = localStorage.getItem('plex-last-username');
        if (lastUsername) scope.setTag('best_guess_user', lastUsername);
      }

      Object.entries(tags).forEach(([key, value]) => scope.setTag(key, value));
      Object.entries(context).forEach(([key, value]) => scope.setContext(key, value));
      if (extra && typeof extra === "object") scope.setExtras(extra);
      attachments.forEach(attachment => scope.addAttachment(attachment));

      if (optionsData) {
        scope.addAttachment({
          filename: "data.json",
          data: typeof optionsData === "string" ? optionsData : safeStringifyJSON(optionsData),
          contentType: "application/json",
        });
      }

      if (asException) {
        const error = new Error(message);
        error.name = errorName || "UserException";
        if (!captureStackTrace) delete error.stack;
        eventId = Sentry.captureException(error);
      } else {
        const normalizedLevel = /^(log|info|warning|error|fatal|debug|assert)$/.test(level) ? level : "info";
        eventId = Sentry.captureMessage(message, normalizedLevel);
      }
    });

    return eventId;
  } catch (error) {
    console.error(`[Sentry] captureMessage failed:`, error);
    return null;
  }
};

export const stylizePublications = s => {
  return s.replace(/\[([^\]]+)](?=\(pubmed:)/g, (match, text) => RE_PLEX_ID.test(text) ? `[${text}]` : `["${text}"]`)
    .replace(/\[([^\]]+)](?=\(patents:)/g, (match, text) => RE_PLEX_ID.test(text) ? `[${text}]` : `[*${text}*]`);
};

// Split text into alternating [prose, protected, prose, protected, ...] segments.
// Protected segments are fenced code blocks (```...```), inline code (`...`), and
// raw XML/HTML blocks that LLMs sometimes emit without fences (e.g. GraphML, SVG).
// Returns [{text, protected: bool}].
const _RE_PROTECTED_SEGMENTS = new RegExp(
  // Fenced code block (``` ... ```)
  '```[\\s\\S]*?```'
  // Inline code span (` ... ` on one line)
  + '|`[^`\\n]+`'
  // Raw XML: optional <?xml ...?> declaration followed by a recognised root element
  // Matches from <?xml or <rootElement through the matching </rootElement>
  + '|(?:' + '<\\?xml[^?]*\\?>[\\s\\S]*?)?'
    + '<(graphml|svg|html|rss|feed|opml)\\b[\\s\\S]*?<\\/\\1>'
  , 'g'
);

const splitProtectedSegments = s => {
  const segments = [];
  // Reset lastIndex since we reuse the compiled regex
  _RE_PROTECTED_SEGMENTS.lastIndex = 0;
  let last = 0;
  let m;
  while ((m = _RE_PROTECTED_SEGMENTS.exec(s)) !== null) {
    if (m.index > last) segments.push({ text: s.slice(last, m.index), protected: false });
    segments.push({ text: m[0], protected: true });
    last = m.index + m[0].length;
  }
  if (last < s.length) segments.push({ text: s.slice(last), protected: false });
  return segments;
};

export const linkifyPlexIds = s => {
  // Convert to MD links any bare plex IDs (not already within an MD link).
  // Skips fenced/inline code blocks and raw XML blocks to avoid corrupting structured data.
  return splitProtectedSegments(s).map(seg => {
    if (seg.protected) return seg.text;
    return seg.text.replace(/(?<!]\()(?<!\[)(\b[a-z][-_0-9a-z]+:[^\s,;'"\\)\]]+\b)(?!\])/mg,
                     (match, id, offset) => {
                       const beforeText = seg.text.substring(0, offset);
                       const openBrackets = (beforeText.match(/\[/g) || []).length;
                       const closeBrackets = (beforeText.match(/\]/g) || []).length;
                       if (openBrackets > closeBrackets) return match;
                       if (/\]\([^)]*$/.test(beforeText)) return match;
                       if (!RE_PLEX_ID.test(id)) return match;
                       return `[${id}](${id})`;
                     });
  }).join('');
}

// Adjust legacy IDs to current ones
// Call this just before making an API call or otherwise generating a URL
export const updateLegacyId = (id) => {
  if (!RE_PLEX_ID.test(id)) {
    return id;
  }
  id = id
    .replace("entrez_gene:", "entrezgene:")
    .replace("open_targets:", "opentargets:")
    .replace("patent:", "patents:")
    .replace("company:", "orgs:");
  if (!legacyGS) {
    // remove gs- or cs- prefixes on non-custom datasets
    // NOTE: only do this if the dataset is 1.4.2+
    id = id.replace(/((ccle[a-z]+|gpsa|cdcode|moaa):)[cg]s-/, "$1")
      // old orcs, ccle, and gxa geneset IDs ONLY (not decrypte, moaa, or other datasets)
      .replace(/^(orcs|ccle[a-z]+|gxa):(.+):(up|dn|gain|loss)$/, "$1:$2|$3");
  }
  // Going from 1.4.2b (beta) to 1.4.2a (auto) will fail
  if (!legacyGPSA) {
      // Old-style GPSA genesets; changed in 1.4.2b+
      // Allow any non-underscore characters in both ID parts (handles slashes, etc.)
      id = id.replace(/^(gpsa:[^_]+)_[^_]+_UP$/, "$1|up")
        .replace(/^(gpsa:[^_]+)_[^_]+_DOWN$/, "$1|dn");
  }
  return id;
}

// Adjust legacy IDs to current ones
// Call this just before making an API call or otherwise generating a URL
export const updateLegacyIDs = ids =>ids.map(id => updateLegacyId(id));

export const hash = s => (s || "").split('').reduce((prev, curr) => Math.imul(31, prev) + curr.charCodeAt(0) | 0, 0);

export const abbreviateLines = (text, lines = 16) => {
  const split = text.split(/\n/);
  if (split.length > lines) {
    return `${split.slice(0, lines).join("\n")}...`;
  }
  return text;
};



// Taken from npm package content-disposition, with bugfixes applied
export const parseContentDisposition = (string) => {

  // RegExp to match percent encoding escape.
  const HEX_ESCAPE_REPLACE_REGEXP = /%([0-9A-Fa-f]{2})/g;
  // RegExp to match non-latin1 characters.
  const NON_LATIN1_REGEXP = /[^\x20-\x7e\xa0-\xff]/g;
  // RegExp to match quoted-pair in RFC 2616
  const QESC_REGEXP = /\\([\u0000-\u007f])/g;

  /**
   * RegExp for various RFC 2616 grammar
   *
   * parameter     = token "=" ( token | quoted-string )
   * token         = 1*<any CHAR except CTLs or separators>
   * separators    = "(" | ")" | "<" | ">" | "@"
   *               | "," | ";" | ":" | "\" | <">
   *               | "/" | "[" | "]" | "?" | "="
   *               | "{" | "}" | SP | HT
   * quoted-string = ( <"> *(qdtext | quoted-pair ) <"> )
   * qdtext        = <any TEXT except <">>
   * quoted-pair   = "\" CHAR
   * CHAR          = <any US-ASCII character (octets 0 - 127)>
   * TEXT          = <any OCTET except CTLs, but including LWS>
   * LWS           = [CRLF] 1*( SP | HT )
   * CRLF          = CR LF
   * CR            = <US-ASCII CR, carriage return (13)>
   * LF            = <US-ASCII LF, linefeed (10)>
   * SP            = <US-ASCII SP, space (32)>
   * HT            = <US-ASCII HT, horizontal-tab (9)>
   * CTL           = <any US-ASCII control character (octets 0 - 31) and DEL (127)>
   * OCTET         = <any 8-bit sequence of data>
   * @private
   */
  const PARAM_REGEXP = /;[\x09\x20]*([!#$%&'*+.0-9A-Z^_`a-z|~-]+)[\x09\x20]*\*?=[\x09\x20]*("(?:[\x20!\x23-\x5b\x5d-\x7e\x80-\xff]|\\[\x20-\x7e])*"|[()!#$%&'*+.0-9A-Z^_`a-z|~-]+)[\x09\x20]*/g;

  /**
   * RegExp for various RFC 5987 grammar
   *
   * ext-value     = charset  "'" [ language ] "'" value-chars
   * charset       = "UTF-8" / "ISO-8859-1" / mime-charset
   * mime-charset  = 1*mime-charsetc
   * mime-charsetc = ALPHA / DIGIT
   *               / "!" / "#" / "$" / "%" / "&"
   *               / "+" / "-" / "^" / "_" / "`"
   *               / "{" / "}" / "~"
   * language      = ( 2*3ALPHA [ extlang ] )
   *               / 4ALPHA
   *               / 5*8ALPHA
   * extlang       = *3( "-" 3ALPHA )
   * value-chars   = *( pct-encoded / attr-char )
   * pct-encoded   = "%" HEXDIG HEXDIG
   * attr-char     = ALPHA / DIGIT
   *               / "!" / "#" / "$" / "&" / "+" / "-" / "."
   *               / "^" / "_" / "`" / "|" / "~"
   * @private
   */
  const EXT_VALUE_REGEXP = /^([A-Za-z0-9!#$%&+\-^_`{}~]+)'(?:[A-Za-z]{2,3}(?:-[A-Za-z]{3}){0,3}|[A-Za-z]{4,8}|)'((?:%[0-9A-Fa-f]{2}|[()A-Za-z0-9!#$&+.^_`|~-])+)$/;

  /**
   * RegExp for various RFC 6266 grammar
   *
   * disposition-type = "inline" | "attachment" | disp-ext-type
   * disp-ext-type    = token
   * disposition-parm = filename-parm | disp-ext-parm
   * filename-parm    = "filename" "=" value
   *                  | "filename*" "=" ext-value
   * disp-ext-parm    = token "=" value
   *                  | ext-token "=" ext-value
   * ext-token        = <the characters in token, followed by "*">
   * @private
   */
  const DISPOSITION_TYPE_REGEXP = /^([!#$%&'*+.0-9A-Z^_`a-z|~-]+)[\x09\x20]*(?:$|;)/;

  /**
   * Decode a RFC 5987 field value (gracefully).
   *
   * @param {string} str
   * @return {string}
   * @private
   */

  const decodefield  = (str) => {
    const match = EXT_VALUE_REGEXP.exec(str.replace(/^"(.*)"$/, "$1"));

    if (!match) {
      throw new TypeError('invalid extended field value')
    }

    const charset = match[1].toLowerCase();
    const encoded = match[2];

    // to binary string
    const binary = encoded.replace(HEX_ESCAPE_REPLACE_REGEXP, pdecode);

    switch (charset) {
      case 'iso-8859-1':
        return getlatin1(binary);
      case 'utf-8':
      case 'utf8':
        return Buffer.from(binary, 'binary').toString('utf8');
      default:
        throw new TypeError('unsupported charset in extended field')
    }
  }

  /**
   * Get ISO-8859-1 version of string.
   *
   * @param {string} val
   * @return {string}
   * @private
   */

  const getlatin1 = (val) => {
    // simple Unicode -> ISO-8859-1 transformation
    return String(val).replace(NON_LATIN1_REGEXP, '?');
  }

  /**
   * Percent decode a single character.
   *
   * @param {string} str
   * @param {string} hex
   * @return {string}
   * @private
   */

  const pdecode = (str, hex)=> {
    return String.fromCharCode(parseInt(hex, 16));
  }

  /**
   * Percent encode a single character.
   *
   * @param {string} char
   * @return {string}
   * @private
   */
  const pencode = (char) => {
    return '%' + String(char)
      .charCodeAt(0)
      .toString(16)
      .toUpperCase();
  }

  if (!string || typeof string !== 'string') {
    throw new TypeError('argument string is required')
  }

  let match = DISPOSITION_TYPE_REGEXP.exec(string);

  if (!match) {
    throw new TypeError('invalid type format')
  }

  // normalize type
  let index = match[0].length;
  const type = match[1].toLowerCase();

  let key;
  const names = [];
  const params = {};
  let value;

  // calculate index to start at
  index = PARAM_REGEXP.lastIndex = match[0].slice(-1) === ';'
                                   ? index - 1
                                   : index;

  // match parameters
  while ((match = PARAM_REGEXP.exec(string))) {
    if (match.index !== index) {
      throw new TypeError('invalid parameter format')
    }

    index += match[0].length;
    key = match[1].toLowerCase();
    value = match[2];

    if (names.indexOf(key) !== -1) {
      throw new TypeError('invalid duplicate parameter')
    }

    names.push(key);

    if (key.indexOf('*') + 1 === key.length) {
      // decode extended value
      key = key.slice(0, -1);
      value = decodefield(value);

      // overwrite existing value
      params[key] = value;
      continue
    }

    if (typeof params[key] === 'string') {
      continue
    }

    if (value[0] === '"') {
      // remove quotes and escapes
      value = value
        .slice(1, -1)
        .replace(QESC_REGEXP, '$1');
    }

    params[key] = value;
  }

  if (index !== -1 && index !== string.length) {
    throw new TypeError('invalid parameter format');
  }

  return {type, parameters: params};
};

export const safeStringifyJSON = obj => {
  try {
    return JSON.stringify(obj);
  }
  catch(e) {
    return `${obj}`;
  }
}

export const safeParseJSON = text => {
  try {
    return JSON.parse(text);
  } catch (e) {
    return null;
  }
};
